import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Table,
  Pagination,
  Icon,
  Dropdown,
  Modal,
  Form,
  Message,
  Segment,
  Label,
  Divider,
  Grid,
  Statistic,
  Header,
  Card,
  Tab,
  Popup,
  TextArea
} from 'semantic-ui-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';
import './index.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658'];

// ========== 代码查看器组件 - 支持行号和复制 ==========
const CodeViewer = ({ code, maxHeight = '500px', showLineNumbers = true, onCopy }) => {
  const lines = (code || '').split('\n');
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code || '');
    if (onCopy) onCopy();
  };
  
  return (
    <div style={{ position: 'relative' }}>
      <Button 
        icon='copy' 
        size='mini'
        content='复制'
        style={{ 
          position: 'absolute', 
          top: 8, 
          right: 8, 
          zIndex: 10,
          opacity: 0.9
        }}
        onClick={handleCopy}
      />
      <div style={{
        display: 'flex',
        backgroundColor: '#1e1e1e',
        borderRadius: '8px',
        overflow: 'auto',
        maxHeight: maxHeight,
        border: '1px solid #3c3c3c'
      }}>
        {showLineNumbers && (
          <div style={{
            padding: '16px 12px',
            color: '#858585',
            textAlign: 'right',
            userSelect: 'none',
            borderRight: '1px solid #3c3c3c',
            fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
            fontSize: '13px',
            lineHeight: '1.6',
            minWidth: '45px',
            backgroundColor: '#252526'
          }}>
            {lines.map((_, i) => <div key={i}>{i + 1}</div>)}
          </div>
        )}
        <pre style={{
          flex: 1,
          margin: 0,
          padding: '16px',
          color: '#d4d4d4',
          fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
          fontSize: '13px',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          backgroundColor: '#1e1e1e'
        }}>
          {code}
        </pre>
      </div>
    </div>
  );
};

// ========== 代码编辑器组件 - 带行号的可编辑组件 ==========
const CodeEditor = ({ value, onChange, placeholder, className, rows = 25, minHeight = '500px' }) => {
  const lines = (value || '').split('\n');
  const lineCount = Math.max(lines.length, rows);
  const lineHeight = 22.4; // 与 TextArea 的 line-height 匹配
  
  // 同步滚动
  const handleScroll = (e) => {
    const container = e.target.closest('.code-editor-container');
    if (container) {
      const lineNumbers = container.querySelector('.code-editor-line-numbers');
      if (lineNumbers) {
        lineNumbers.scrollTop = e.target.scrollTop;
      }
    }
  };
  
  return (
    <div 
      className='code-editor-container'
      style={{
        display: 'flex',
        backgroundColor: '#1e1e1e',
        borderRadius: '8px',
        border: '1px solid #3c3c3c',
        overflow: 'hidden',
        minHeight: minHeight,
        position: 'relative'
      }}
    >
      {/* 行号 */}
      <div 
        className='code-editor-line-numbers'
        style={{
          padding: '12px 8px',
          color: '#858585',
          textAlign: 'right',
          userSelect: 'none',
          borderRight: '1px solid #3c3c3c',
          fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
          fontSize: '14px',
          lineHeight: `${lineHeight}px`,
          minWidth: '50px',
          backgroundColor: '#252526',
          overflowY: 'hidden',
          overflowX: 'hidden'
        }}
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i} style={{ height: `${lineHeight}px`, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>{i + 1}</div>
        ))}
      </div>
      
      {/* 编辑区域 */}
      <TextArea
        className={className}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onScroll={handleScroll}
        style={{
          flex: 1,
          margin: 0,
          padding: '12px',
          color: '#d4d4d4',
          fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
          fontSize: '14px',
          lineHeight: `${lineHeight}px`,
          backgroundColor: '#1e1e1e',
          border: 'none',
          borderRadius: 0,
          resize: 'vertical',
          minHeight: minHeight,
          width: '100%',
          overflowY: 'auto'
        }}
      />
    </div>
  );
};

// ========== 可复制文本框组件 ==========
const CopyableText = ({ text, label, maxHeight = '150px' }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(text || '');
    showSuccess('已复制到剪贴板');
  };
  
  return (
    <div style={{ marginBottom: '12px' }}>
      {label && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <strong>{label}</strong>
          <Button icon='copy' size='mini' onClick={handleCopy} title='复制' />
        </div>
      )}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '12px',
        borderRadius: '4px',
        fontFamily: 'monospace',
        fontSize: '13px',
        maxHeight: maxHeight,
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        border: '1px solid #ddd'
      }}>
        {text || '-'}
      </div>
    </div>
  );
};

const DynamicPrompt = () => {
  const [prompts, setPrompts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [language, setLanguage] = useState('');
  const [status, setStatus] = useState('');
  const [statistics, setStatistics] = useState(null);

  // 创建步骤管理
  const [createStep, setCreateStep] = useState(1); // 1: 基本信息, 2: 参数配置, 3: 模板编辑
  const [createStepData, setCreateStepData] = useState({
    // Step 1
    api_name: '',
    name: '',
    description: '',
    category: '',
    language: 'zh',
    // Step 2
    parameters: {
      extractors: [],
      validation: {},
      defaults: {}
    },
    // Step 3
    template: ''
  });

  // 参数编辑
  const [currentExtractor, setCurrentExtractor] = useState(null);
  const [showExtractorModal, setShowExtractorModal] = useState(false);

  // 创建/编辑表单数据
  const [formData, setFormData] = useState({
    api_name: '',
    name: '',
    description: '',
    category: '',
    language: 'python',
    template: '',
    parameters: null
  });

  // 版本表单数据
  const [versionFormData, setVersionFormData] = useState({
    version: '',
    template: '',
    parameters: null,
    change_log: ''
  });

  // 版本创建多步骤
  const [versionStep, setVersionStep] = useState(1); // 1: 基本信息, 2: 参数配置, 3: 模板编辑
  const [versionParams, setVersionParams] = useState({
    extractors: [],
    validation: {},
    defaults: {}
  });
  const [currentVersionExtractor, setCurrentVersionExtractor] = useState(null);
  const [showVersionExtractorModal, setShowVersionExtractorModal] = useState(false);

  // 导入导出相关状态
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('create');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSelectedIds, setExportSelectedIds] = useState([]);

  // 版本继承相关状态
  const [inheritFromVersionId, setInheritFromVersionId] = useState(null);

  // 版本详情查看
  const [showVersionDetailModal, setShowVersionDetailModal] = useState(false);
  const [versionDetailData, setVersionDetailData] = useState(null);

  // 测试数据
  const [testLoading, setTestLoading] = useState(false);
  const [testMessages, setTestMessages] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [availableTokens, setAvailableTokens] = useState([]);
  const [selectedTestToken, setSelectedTestToken] = useState('');

  useEffect(() => {
    loadPrompts();
  }, [page, search, language, status]);

  useEffect(() => {
    if (showStatistics) {
      loadStatistics();
    }
  }, [showStatistics]);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page,
        page_size: pageSize,
        search,
        language,
        status
      });
      const res = await API.get(`/api/admin/prompts?${query}`);
      if (res.data.success) {
        setPrompts(res.data.data.prompts || []);
        setTotal(res.data.data.total || 0);
      } else {
        showError(res.data.message || '获取列表失败');
      }
    } catch (error) {
      showError('获取列表失败: ' + error.message);
    }
    setLoading(false);
  };

  const loadStatistics = async () => {
    try {
      const res = await API.get('/api/admin/prompts/statistics');
      if (res.data.success) {
        setStatistics(res.data.data);
      } else {
        showError(res.data.message || '获取统计数据失败');
      }
    } catch (error) {
      showError('获取统计数据失败: ' + error.message);
    }
  };

  // ===== 创建流程 =====
  const handleStartCreate = () => {
    setCreateStep(1);
    setCreateStepData({
      api_name: '',
      name: '',
      description: '',
      category: '',
      language: 'zh',
      parameters: {
        extractors: [],
        validation: {},
        defaults: {}
      },
      template: ''
    });
    setShowCreateModal(true);
  };

  const handleNextCreateStep = () => {
    if (createStep === 1) {
      if (!createStepData.api_name || !createStepData.name || !createStepData.language) {
        showError('请填写必需字段');
        return;
      }
      setCreateStep(2);
    } else if (createStep === 2) {
      if (createStepData.parameters.extractors.length === 0) {
        showError('至少需要一个参数');
        return;
      }
      setCreateStep(3);
    }
  };

  const handlePrevCreateStep = () => {
    if (createStep > 1) {
      setCreateStep(createStep - 1);
    }
  };

  const handleFinishCreate = async () => {
    if (!createStepData.template.trim()) {
      showError('模板不能为空');
      return;
    }

    try {
      // 将参数数组转换为对象格式（后端期望的格式）
      const extractorsObj = {};
      createStepData.parameters.extractors.forEach(ext => {
        extractorsObj[ext.name] = {
          type: ext.type,
          source: ext.source || '',
          index: ext.index || 0,
          field: ext.field || '',
          required: ext.required,
          description: ext.description || '',
          transform: ext.transform || ''
        };
      });

      const res = await API.post('/api/admin/prompts', {
        api_name: createStepData.api_name,
        name: createStepData.name,
        description: createStepData.description,
        category: createStepData.category,
        language: createStepData.language,
        template: createStepData.template,
        parameters: {
          extractors: extractorsObj,
          validation: createStepData.parameters.validation || {},
          defaults: createStepData.parameters.defaults || {}
        }
      });

      if (res.data.success) {
        showSuccess('创建成功');
        setShowCreateModal(false);
        loadPrompts();
      } else {
        showError(res.data.message || '创建失败');
      }
    } catch (error) {
      showError('创建失败: ' + error.message);
    }
  };

  // ===== 参数编辑 =====
  const handleAddExtractor = () => {
    // 自动设置索引为当前参数列表的长度（自动递增）
    const nextIndex = createStepData.parameters.extractors.length;
    setCurrentExtractor({
      name: '',
      type: 'message_index',
      index: nextIndex,  // 自动递增索引
      required: true,
      description: '',
      transform: ''
    });
    setShowExtractorModal(true);
  };

  const handleEditExtractor = (index) => {
    setCurrentExtractor({ ...createStepData.parameters.extractors[index], _index: index });
    setShowExtractorModal(true);
  };

  const handleSaveExtractor = () => {
    if (!currentExtractor.name) {
      showError('参数名称不能为空');
      return;
    }

    const extractors = [...createStepData.parameters.extractors];
    if (currentExtractor._index !== undefined) {
      extractors[currentExtractor._index] = { ...currentExtractor };
    } else {
      extractors.push({ ...currentExtractor });
    }

    setCreateStepData({
      ...createStepData,
      parameters: {
        ...createStepData.parameters,
        extractors
      }
    });

    setShowExtractorModal(false);
    setCurrentExtractor(null);
  };

  const handleDeleteExtractor = (index) => {
    const extractors = createStepData.parameters.extractors.filter((_, i) => i !== index);
    setCreateStepData({
      ...createStepData,
      parameters: {
        ...createStepData.parameters,
        extractors
      }
    });
  };

  // ===== 测试 =====
  const handleOpenTestModal = async (version) => {
    setSelectedVersion(version);
    setTestMessages([]);
    setTestResult(null);
    setAvailableModels([]);
    setSelectedModel('');
    setAvailableTokens([]);
    setSelectedTestToken('');
    setShowTestModal(true);

    // 获取用户的 token 列表
    try {
      const tokensRes = await API.get('/api/token');
      if (tokensRes.data.success) {
        const tokens = tokensRes.data.data || [];
        if (tokens.length === 0) {
          showError('获取 token 失败：无可用的 token');
          return;
        }
        setAvailableTokens(tokens);
        // 默认选择第一个 token
        setSelectedTestToken(tokens[0].key);
      } else {
        showError(tokensRes.data.message || '获取 token 失败');
      }
    } catch (error) {
      showError('获取 token 失败: ' + error.message);
    }
  };

  const handleAddTestMessage = (paramName) => {
    setTestMessages([
      ...testMessages,
      {
        param_name: paramName,
        role: 'user',
        content: ''
      }
    ]);
  };

  const handleUpdateTestMessage = (index, content) => {
    const messages = [...testMessages];
    messages[index].content = content;
    setTestMessages(messages);
  };

  const handleRemoveTestMessage = (index) => {
    setTestMessages(testMessages.filter((_, i) => i !== index));
  };

  const handleTestPrompt = async () => {
    if (!selectedPrompt || !selectedVersion || testMessages.length === 0) {
      showError('请输入测试消息');
      return;
    }

    if (!selectedModel) {
      showError('请选择模型');
      return;
    }

    if (!selectedTestToken) {
      showError('请选择 token');
      return;
    }

    setTestLoading(true);
    try {
      // 1. 构建用户消息（这些消息会被后端的参数提取器处理）
      const userMessages = testMessages.map(msg => ({
        role: msg.role || 'user',
        content: msg.content
      }));

      // 2. 直接调用动态 Prompt API 路由
      // 后端会自动根据参数配置提取参数、渲染模板、调用模型
      const apiUrl = `/v1/dynamic_prompt/${selectedPrompt.api_name}/${selectedPrompt.language}/chat/completions`;
      
      const startTime = Date.now();
      
      // 3. 使用选定的 token 作为 Authorization header
      // 这样不会影响 web 页面的登录状态
      const res = await axios.post(apiUrl, {
        model: selectedModel,
        messages: userMessages,
        temperature: 0.7,
        max_tokens: 1000,
        stream: false
      }, {
        headers: {
          'Authorization': `Bearer ${selectedTestToken}`,
          'Content-Type': 'application/json'
        }
      });

      const executionTime = Date.now() - startTime;

      if (!res.data.choices || res.data.choices.length === 0) {
        showError('API 调用失败: ' + (res.data.error?.message || '未知错误'));
        setTestLoading(false);
        return;
      }

      // 4. 提取模型回复
      const modelReply = res.data.choices[0].message?.content || '无回复';
      const tokensUsed = res.data.usage?.completion_tokens || 0;
      const totalTokens = res.data.usage?.total_tokens || 0;

      // 5. 显示结果
      setTestResult({
        model_reply: modelReply,
        tokens_used: tokensUsed,
        tokens_total: totalTokens,
        execution_time: executionTime,
        model_name: selectedModel,
        user_messages: userMessages
      });

      showSuccess('测试完成');
    } catch (error) {
      showError('测试失败: ' + error.message);
    }
    setTestLoading(false);
  };

  // ===== 版本管理 =====
  const handleOpenVersionModal = async (prompt) => {
    setSelectedPrompt(prompt);
    // 重置版本创建表单
    setVersionStep(1);
    setVersionFormData({
      version: '',
      template: '',
      parameters: null,
      change_log: ''
    });
    setVersionParams({
      extractors: [],
      validation: {},
      defaults: {}
    });
    setInheritFromVersionId(null);
    
    try {
      const res = await API.get(`/api/admin/prompts/${prompt.id}/versions`);
      if (res.data.success) {
        const versionsList = res.data.data || [];
        setVersions(versionsList);
        
        // 自动加载活跃版本作为新版本的基础模板
        const activeVersion = versionsList.find(v => v.is_active);
        if (activeVersion) {
          // 静默加载活跃版本内容（不显示成功提示）
          try {
            const versionRes = await API.get(`/api/admin/prompts/${prompt.id}/versions/${activeVersion.id}/preview`);
            if (versionRes.data.success) {
              const versionData = versionRes.data.data;
              
              // 解析参数
              const params = versionData.parameters || {};
              const extractorsArray = [];
              
              if (params.extractors) {
                Object.entries(params.extractors).forEach(([name, extractor]) => {
                  extractorsArray.push({
                    name,
                    type: extractor.type || 'message_index',
                    source: extractor.source || '',
                    index: extractor.index || 0,
                    field: extractor.field || '',
                    required: extractor.required !== false,
                    description: extractor.description || '',
                    transform: extractor.transform || ''
                  });
                });
              }
              
              // 预填充表单（继承自活跃版本）
              setVersionFormData({
                version: '',
                template: versionData.template || '',
                change_log: `基于活跃版本 ${activeVersion.version} 修改`
              });
              
              setVersionParams({
                extractors: extractorsArray,
                validation: params.validation || {},
                defaults: params.defaults || {}
              });
              
              setInheritFromVersionId(activeVersion.id);
            }
          } catch (e) {
            // 静默失败，不影响正常流程
            console.warn('自动加载活跃版本失败:', e);
          }
        }
      } else {
        showError('获取版本列表失败');
      }
    } catch (error) {
      showError('获取版本列表失败: ' + error.message);
    }
    setShowVersionModal(true);
  };

  const handleCreateVersion = async () => {
    if (!selectedPrompt || !versionFormData.version || !versionFormData.template) {
      showError('请填写必需字段');
      return;
    }

    if (versionParams.extractors.length === 0) {
      showError('至少需要一个参数');
      return;
    }

    try {
      // 将参数数组转换为对象格式
      const extractorsObj = {};
      versionParams.extractors.forEach(ext => {
        extractorsObj[ext.name] = {
          type: ext.type,
          source: ext.source || '',
          index: ext.index || 0,
          field: ext.field || '',
          required: ext.required,
          description: ext.description || '',
          transform: ext.transform || ''
        };
      });

      const res = await API.post(`/api/admin/prompts/${selectedPrompt.id}/versions`, {
        version: versionFormData.version,
        template: versionFormData.template,
        parameters: {
          extractors: extractorsObj,
          validation: versionParams.validation || {},
          defaults: versionParams.defaults || {}
        },
        change_log: versionFormData.change_log
      });

      if (res.data.success) {
        showSuccess('创建版本成功');
        setVersionFormData({
          version: '',
          template: '',
          parameters: null,
          change_log: ''
        });
        setVersionParams({
          extractors: [],
          validation: {},
          defaults: {}
        });
        setVersionStep(1);
        handleOpenVersionModal(selectedPrompt);
      } else {
        showError(res.data.message || '创建版本失败');
      }
    } catch (error) {
      showError('创建版本失败: ' + error.message);
    }
  };

  // 版本参数编辑函数
  const handleAddVersionExtractor = () => {
    const nextIndex = versionParams.extractors.length;
    setCurrentVersionExtractor({
      name: '',
      type: 'message_index',
      index: nextIndex,
      required: true,
      description: '',
      transform: ''
    });
    setShowVersionExtractorModal(true);
  };

  const handleEditVersionExtractor = (index) => {
    setCurrentVersionExtractor({ ...versionParams.extractors[index], _index: index });
    setShowVersionExtractorModal(true);
  };

  const handleSaveVersionExtractor = () => {
    if (!currentVersionExtractor.name) {
      showError('参数名称不能为空');
      return;
    }

    const extractors = [...versionParams.extractors];
    if (currentVersionExtractor._index !== undefined) {
      extractors[currentVersionExtractor._index] = { ...currentVersionExtractor };
    } else {
      extractors.push({ ...currentVersionExtractor });
    }

    setVersionParams({
      ...versionParams,
      extractors
    });

    setShowVersionExtractorModal(false);
    setCurrentVersionExtractor(null);
  };

  const handleDeleteVersionExtractor = (index) => {
    const extractors = versionParams.extractors.filter((_, i) => i !== index);
    setVersionParams({
      ...versionParams,
      extractors
    });
  };

  const handleNextVersionStep = () => {
    if (versionStep === 1) {
      if (!versionFormData.version) {
        showError('请填写版本号');
        return;
      }
      setVersionStep(2);
    } else if (versionStep === 2) {
      if (versionParams.extractors.length === 0) {
        showError('至少需要一个参数');
        return;
      }
      setVersionStep(3);
    }
  };

  const handlePrevVersionStep = () => {
    if (versionStep > 1) {
      setVersionStep(versionStep - 1);
    }
  };

  // ===== 版本详情查看功能 =====
  const handleViewVersionDetail = async (version) => {
    try {
      const res = await API.get(`/api/admin/prompts/${selectedPrompt.id}/versions/${version.id}/preview`);
      if (res.data.success) {
        setVersionDetailData({
          version: version.version,
          template: res.data.data.template,
          parameters: res.data.data.parameters,
          change_log: res.data.data.change_log,
          is_active: version.is_active,
          usage_count: version.usage_count,
          created_at: version.created_at
        });
        setShowVersionDetailModal(true);
      } else {
        showError(res.data.message || '获取版本详情失败');
      }
    } catch (error) {
      showError('获取版本详情失败: ' + error.message);
    }
  };

  // ===== 版本继承功能 =====
  const handleInheritFromVersion = async (versionId) => {
    try {
      const res = await API.get(`/api/admin/prompts/${selectedPrompt.id}/versions/${versionId}/preview`);
      if (res.data.success) {
        const versionData = res.data.data;
        
        // 解析参数（从对象格式转为数组格式）
        const params = versionData.parameters || {};
        const extractorsArray = [];
        
        if (params.extractors) {
          Object.entries(params.extractors).forEach(([name, extractor]) => {
            extractorsArray.push({
              name,
              type: extractor.type || 'message_index',
              source: extractor.source || '',
              index: extractor.index || 0,
              field: extractor.field || '',
              required: extractor.required !== false,
              description: extractor.description || '',
              transform: extractor.transform || ''
            });
          });
        }
        
        // 预填充表单
        setVersionFormData({
          version: '', // 需要用户手动输入新版本号
          template: versionData.template || '',
          change_log: `基于版本 ${versionData.version} 修改`
        });
        
        setVersionParams({
          extractors: extractorsArray,
          validation: params.validation || {},
          defaults: params.defaults || {}
        });
        
        setInheritFromVersionId(versionId);
        setVersionStep(1);
        showSuccess('已加载版本内容，请输入新版本号');
      } else {
        showError('加载版本信息失败');
      }
    } catch (error) {
      showError('加载版本信息失败: ' + error.message);
    }
  };

  // ===== 导入导出功能 =====
  const handleExportPrompts = async () => {
    if (exportSelectedIds.length === 0) {
      showError('请选择至少一个Prompt');
      return;
    }

    try {
      const res = await API.post('/api/admin/prompts/export', {
        prompt_ids: exportSelectedIds
      });
      
      if (res.data.success) {
        // 创建Blob并触发下载
        const jsonData = JSON.stringify(res.data.data, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `prompts_${Date.now()}.json`;
        link.click();
        URL.revokeObjectURL(url);
        showSuccess('导出成功');
        setShowExportModal(false);
        setExportSelectedIds([]);
      } else {
        showError(res.data.message || '导出失败');
      }
    } catch (error) {
      showError('导出失败: ' + error.message);
    }
  };

  const handleImportPrompts = async (file) => {
    if (!file) {
      showError('请选择文件');
      return;
    }

    try {
      const fileContent = await file.text();
      
      // 验证JSON格式
      try {
        JSON.parse(fileContent);
      } catch (e) {
        showError('JSON格式不正确: ' + e.message);
        return;
      }

      const res = await API.post('/api/admin/prompts/import', {
        json_data: fileContent,
        mode: importMode
      });
      
      if (res.data && res.data.success) {
        showSuccess(res.data.message || '导入成功');
        setShowImportModal(false);
        setImportMode('create');
        loadPrompts();
      } else if (res.data) {
        showError(res.data.message || '导入失败');
      }
    } catch (error) {
      // API错误已经在拦截器中显示，这里不需要再显示
      console.error('导入失败:', error);
    }
  };

  const handleActivateVersion = async (versionId) => {
    if (!selectedPrompt) return;

    try {
      const res = await API.post(`/api/admin/prompts/${selectedPrompt.id}/activate/${versionId}`);

      if (res.data.success) {
        showSuccess('激活版本成功');
        handleOpenVersionModal(selectedPrompt);
      } else {
        showError(res.data.message || '激活版本失败');
      }
    } catch (error) {
      showError('激活版本失败: ' + error.message);
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (!selectedPrompt || !window.confirm('确定删除此版本吗？')) return;

    try {
      const res = await API.delete(`/api/admin/prompts/${selectedPrompt.id}/versions/${versionId}`);

      if (res.data.success) {
        showSuccess('删除版本成功');
        handleOpenVersionModal(selectedPrompt);
      } else {
        showError(res.data.message || '删除版本失败');
      }
    } catch (error) {
      showError('删除版本失败: ' + error.message);
    }
  };

  const handleOpenEditModal = (prompt) => {
    setSelectedPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description,
      category: prompt.category,
      status: prompt.status
    });
    setShowEditModal(true);
  };

  const handleUpdatePrompt = async () => {
    if (!selectedPrompt) return;

    try {
      const res = await API.put(`/api/admin/prompts/${selectedPrompt.id}`, {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        status: formData.status
      });

      if (res.data.success) {
        showSuccess('更新成功');
        setShowEditModal(false);
        loadPrompts();
      } else {
        showError(res.data.message || '更新失败');
      }
    } catch (error) {
      showError('更新失败: ' + error.message);
    }
  };

  const handleDeletePrompt = async (id) => {
    if (!window.confirm('确定删除此Prompt吗？')) return;

    try {
      const res = await API.delete(`/api/admin/prompts/${id}`);

      if (res.data.success) {
        showSuccess('删除成功');
        loadPrompts();
      } else {
        showError(res.data.message || '删除失败');
      }
    } catch (error) {
      showError('删除失败: ' + error.message);
    }
  };

  const languageOptions = [
    { key: 'zh', value: 'zh', text: '中文' },
    { key: 'en', value: 'en', text: 'English' }
  ];

  const statusOptions = [
    { key: 1, value: 1, text: '启用' },
    { key: 0, value: 0, text: '禁用' }
  ];

  const extractorTypeOptions = [
    { key: 'message_index', value: 'message_index', text: '按消息索引' },
    { key: 'message_content', value: 'message_content', text: '按消息角色' },
    { key: 'json_field', value: 'json_field', text: 'JSON字段' },
    { key: 'computed', value: 'computed', text: '计算类型' }
  ];

  const transformOptions = [
    { key: '', value: '', text: '无' },
    { key: 'upper', value: 'upper', text: '转大写' },
    { key: 'lower', value: 'lower', text: '转小写' },
    { key: 'trim', value: 'trim', text: '去空格' },
    { key: 'escape', value: 'escape', text: 'HTML转义' }
  ];

  // 获取可用参数列表
  const getAvailableParameters = () => {
    if (createStep === 3) {
      return createStepData.parameters.extractors.map(e => e.name);
    }
    if (selectedVersion) {
      try {
        const params = JSON.parse(selectedVersion.parameters || '{}');
        // 后端返回的是对象格式，需要转换为数组
        const extractors = params.extractors || {};
        return Object.keys(extractors);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  // 分析模板中使用的参数
  const analyzeTemplateParams = (template) => {
    const regex = /\{\{\.(\w+)/g;
    const used = new Set();
    let match;
    while ((match = regex.exec(template)) !== null) {
      used.add(match[1]);
    }
    return Array.from(used);
  };

  const usedParams = createStep === 3 ? analyzeTemplateParams(createStepData.template) : [];

  return (
    <Container>
      <Segment loading={loading} style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>
            <Icon name='sliders horizontal' />
            动态Prompt管理
          </h2>
          <Button primary onClick={handleStartCreate} size='large'>
            <Icon name='plus' /> 创建Prompt
          </Button>
        </div>

        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Form.Input
            placeholder='搜索 API 名称或名称...'
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ flex: 1, minWidth: '200px' }}
            icon='search'
          />
          <Dropdown
            placeholder='选择语言'
            clearable
            selection
            options={languageOptions}
            value={language}
            onChange={(e, data) => {
              setLanguage(data.value);
              setPage(1);
            }}
          />
          <Dropdown
            placeholder='选择状态'
            clearable
            selection
            options={statusOptions}
            value={status}
            onChange={(e, data) => {
              setStatus(data.value);
              setPage(1);
            }}
          />
          <Button 
            icon='chart bar'
            onClick={() => setShowStatistics(!showStatistics)}
            content={showStatistics ? '隐藏统计' : '显示统计'}
          />
          <Button
            icon='upload'
            onClick={() => setShowImportModal(true)}
            content='导入'
          />
          <Button
            icon='download'
            onClick={() => setShowExportModal(true)}
            content='导出'
          />
        </div>

        {/* 统计概览 */}
        {statistics && showStatistics && (
          <Segment style={{ marginBottom: '20px', backgroundColor: '#f9f9f9' }}>
            <Header as="h3">
              <Icon name="chart bar" />
              Prompt统计概览
            </Header>
            
            <Grid columns={4} divided stackable style={{ marginBottom: '20px' }}>
              <Grid.Row>
                <Grid.Column>
                  <Statistic>
                    <Statistic.Value>{statistics.total_prompts || 0}</Statistic.Value>
                    <Statistic.Label>总Prompt数</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic>
                    <Statistic.Value>{statistics.total_versions || 0}</Statistic.Value>
                    <Statistic.Label>总版本数</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic>
                    <Statistic.Value>{statistics.total_usage || 0}</Statistic.Value>
                    <Statistic.Label>总使用次数</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic>
                    <Statistic.Value>{(statistics.average_duration || 0).toFixed(0)}</Statistic.Value>
                    <Statistic.Label>平均耗时 (ms)</Statistic.Label>
                  </Statistic>
                </Grid.Column>
              </Grid.Row>
            </Grid>

            <Divider />

            {/* 图表 */}
            {(statistics.usage_by_language?.length > 0 || 
              statistics.top_prompts?.length > 0 || 
              statistics.daily_usage?.length > 0) && (
              <Grid columns={2} stackable>
                {statistics.usage_by_language?.length > 0 && (
                  <Grid.Column>
                    <Segment>
                      <Header as='h4'>语言分布</Header>
                      <ResponsiveContainer width='100%' height={250}>
                        <PieChart>
                          <Pie
                            data={statistics.usage_by_language}
                            dataKey='usage_count'
                            nameKey='language'
                            cx='50%'
                            cy='50%'
                            outerRadius={80}
                            label
                          >
                            {statistics.usage_by_language.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Segment>
                  </Grid.Column>
                )}

                {statistics.top_prompts?.length > 0 && (
                  <Grid.Column>
                    <Segment>
                      <Header as='h4'>Top 10 Prompt</Header>
                      <ResponsiveContainer width='100%' height={250}>
                        <BarChart data={statistics.top_prompts}>
                          <CartesianGrid strokeDasharray='3 3' />
                          <XAxis dataKey='name' angle={-45} textAnchor='end' height={80} />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey='usage_count' fill='#0088FE' />
                        </BarChart>
                      </ResponsiveContainer>
                    </Segment>
                  </Grid.Column>
                )}

                {statistics.daily_usage?.length > 0 && (
                  <Grid.Column width={16}>
                    <Segment>
                      <Header as='h4'>日均使用趋势</Header>
                      <ResponsiveContainer width='100%' height={250}>
                        <LineChart data={statistics.daily_usage}>
                          <CartesianGrid strokeDasharray='3 3' />
                          <XAxis dataKey='date' />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type='monotone' dataKey='usage_count' stroke='#8884d8' dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Segment>
                  </Grid.Column>
                )}
              </Grid>
            )}
          </Segment>
        )}

        {/* Prompt 列表 */}
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>API 名称</Table.HeaderCell>
              <Table.HeaderCell>显示名称</Table.HeaderCell>
              <Table.HeaderCell>语言</Table.HeaderCell>
              <Table.HeaderCell>分类</Table.HeaderCell>
              <Table.HeaderCell>活跃版本</Table.HeaderCell>
              <Table.HeaderCell>状态</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {prompts.map(prompt => (
              <Table.Row key={prompt.id}>
                <Table.Cell><code>{prompt.api_name}</code></Table.Cell>
                <Table.Cell>{prompt.name}</Table.Cell>
                <Table.Cell>{prompt.language}</Table.Cell>
                <Table.Cell>{prompt.category || <span style={{ color: '#ccc' }}>-</span>}</Table.Cell>
                <Table.Cell>
                  {prompt.active_version ? (
                    <Label color='blue' size='small'>{prompt.active_version.version}</Label>
                  ) : (
                    <Label size='small'>无活跃版本</Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {prompt.status === 1 ? (
                    <Label color='green' size='small'>启用</Label>
                  ) : (
                    <Label color='red' size='small'>禁用</Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Button.Group size='small' vertical>
                    <Button
                      icon='edit'
                      content='编辑'
                      onClick={() => handleOpenEditModal(prompt)}
                    />
                    <Button
                      icon='code'
                      content='版本'
                      onClick={() => handleOpenVersionModal(prompt)}
                    />
                    <Button
                      color='red'
                      icon='trash'
                      content='删除'
                      onClick={() => handleDeletePrompt(prompt.id)}
                    />
                  </Button.Group>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        {prompts.length === 0 && (
          <Message info>
            <Message.Header>暂无数据</Message.Header>
            <p>还没有创建任何Prompt，点击右上角"创建Prompt"按钮开始。</p>
          </Message>
        )}

        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
          <Pagination
            activePage={page}
            onPageChange={(e, data) => setPage(data.activePage)}
            totalPages={Math.ceil(total / pageSize)}
            size='mini'
          />
        </div>
      </Segment>

      {/* ===== 创建Prompt多步流程 ===== */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} size='large'>
        <Modal.Header>
          <Icon name='plus' />
          创建动态Prompt - 第 {createStep} 步
        </Modal.Header>
        <Modal.Content scrolling>
          {/* Step 1: 基本信息 */}
          {createStep === 1 && (
            <div>
              <Segment color='blue'>
                <Header as='h4'>
                  <Icon name='info' />
                  第一步：基本信息
                </Header>
                <p>设置 Prompt 的基本信息。API 名称和显示名称是必需的。</p>
              </Segment>

              <Form>
                <Form.Group widths='equal'>
                  <Form.Field>
                    <label>
                      API 名称 *
                      <Popup
                        trigger={<Icon name='help circle' size='small' color='blue' style={{marginLeft: '5px'}} />}
                        content='IDE 通过此名称调用 API，创建后无法修改。使用英文+下划线格式。'
                        position='top center'
                        hoverable
                      />
                    </label>
                    <Form.Input
                      placeholder='例如: code_review'
                      value={createStepData.api_name}
                      onChange={(e) => setCreateStepData({ ...createStepData, api_name: e.target.value })}
                      style={{ padding: '10px 12px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>语言 *</label>
                    <Dropdown
                      selection
                      fluid
                      options={languageOptions}
                      value={createStepData.language}
                      onChange={(e, data) => setCreateStepData({ ...createStepData, language: data.value })}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Field>
                  <label>
                    显示名称 *
                    <Popup
                      trigger={<Icon name='help circle' size='small' color='blue' style={{marginLeft: '5px'}} />}
                      content='在管理页面和用户界面显示的名称，可以使用任何语言，创建后可以修改。'
                      position='top center'
                      hoverable
                    />
                  </label>
                  <Form.Input
                    placeholder='例如: 代码审查'
                    value={createStepData.name}
                    onChange={(e) => setCreateStepData({ ...createStepData, name: e.target.value })}
                    style={{ padding: '10px 12px' }}
                  />
                </Form.Field>

                <Form.Field>
                  <label>描述</label>
                  <Form.TextArea
                    placeholder='描述这个 Prompt 的用途...'
                    value={createStepData.description}
                    onChange={(e) => setCreateStepData({ ...createStepData, description: e.target.value })}
                    rows={6}
                    style={{ 
                      padding: '10px 12px',
                      minHeight: '120px',
                      resize: 'vertical'
                    }}
                  />
                </Form.Field>

                <Form.Field>
                  <label>分类</label>
                  <Form.Input
                    placeholder='例如: code_analysis'
                    value={createStepData.category}
                    onChange={(e) => setCreateStepData({ ...createStepData, category: e.target.value })}
                    style={{ padding: '10px 12px' }}
                  />
                </Form.Field>
              </Form>
            </div>
          )}

          {/* Step 2: 参数配置 */}
          {createStep === 2 && (
            <div>
              <Segment color='green'>
                <Header as='h4'>
                  <Icon name='settings' />
                  第二步：参数配置
                </Header>
                <p>定义如何从请求消息中提取参数。至少需要一个参数。</p>
              </Segment>

              <Button primary onClick={handleAddExtractor} style={{ marginBottom: '15px' }}>
                <Icon name='plus' /> 添加参数
              </Button>

              {createStepData.parameters.extractors.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  {createStepData.parameters.extractors.map((extractor, idx) => (
                    <Segment key={idx} secondary>
                      <Grid>
                        <Grid.Row>
                          <Grid.Column width={12}>
                            <strong>{extractor.name}</strong>
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                              类型: {extractor.type} 
                              {extractor.type === 'message_index' && ` • 索引: ${extractor.index}`}
                              {extractor.required && ' • 必需'}
                              {extractor.description && ` • ${extractor.description}`}
                            </div>
                          </Grid.Column>
                          <Grid.Column width={4} textAlign='right'>
                            <Button icon='edit' size='mini' onClick={() => handleEditExtractor(idx)} />
                            <Button icon='trash' size='mini' negative onClick={() => handleDeleteExtractor(idx)} />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Segment>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: 模板编辑 */}
          {createStep === 3 && (
            <div>
              <Segment color='orange'>
                <Header as='h4'>
                  <Icon name='pencil' />
                  第三步：编辑模板
                </Header>
                <p>使用 Go template 语法编写模板，用 {'{{.参数名}}'} 引用参数。点击参数名可快速插入到模板中。</p>
              </Segment>

              {/* 可用参数提示 - 固定在顶部 */}
              {createStepData.parameters.extractors.length > 0 && (
                <Segment attached='top' color='blue' style={{ marginBottom: '0' }}>
                  <Header as='h5' style={{ marginBottom: '12px' }}>
                    <Icon name='info circle' />
                    可用参数（点击插入）
                  </Header>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {createStepData.parameters.extractors.map((ext, idx) => {
                      const paramCode = `{{.${ext.name}}}`;
                      return (
                        <div
                          key={idx}
                          onClick={() => {
                            const textarea = document.querySelector('.template-textarea');
                            if (textarea) {
                              const start = textarea.selectionStart;
                              const end = textarea.selectionEnd;
                              const text = createStepData.template;
                              const newText = text.substring(0, start) + paramCode + text.substring(end);
                              setCreateStepData({ ...createStepData, template: newText });
                              // 恢复光标位置
                              setTimeout(() => {
                                textarea.focus();
                                textarea.setSelectionRange(start + paramCode.length, start + paramCode.length);
                              }, 0);
                            }
                          }}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: usedParams.includes(ext.name) ? '#e8f5e9' : '#fff3e0',
                            border: `2px solid ${usedParams.includes(ext.name) ? '#4caf50' : '#ff9800'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <code style={{ 
                            fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: '#1976d2'
                          }}>{paramCode}</code>
                          {ext.required ? (
                            <Label size='mini' color='red'>必需</Label>
                          ) : (
                            <Label size='mini'>可选</Label>
                          )}
                          {usedParams.includes(ext.name) && (
                            <Icon name='check circle' color='green' size='small' />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Divider />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    <strong>模板函数：</strong>
                    <code style={{ marginLeft: '8px' }}>{'{{upper .name}}'}</code> 转大写
                    <code style={{ marginLeft: '8px' }}>{'{{lower .name}}'}</code> 转小写
                    <code style={{ marginLeft: '8px' }}>{'{{trim .name}}'}</code> 去空格
                    <code style={{ marginLeft: '8px' }}>{'{{if .name}}...{{end}}'}</code> 条件
                  </div>
                </Segment>
              )}

              {/* 模板输入框 - 全宽 */}
              <Segment attached={createStepData.parameters.extractors.length > 0 ? 'bottom' : undefined} style={{ marginTop: '0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <Header as='h5' style={{ margin: 0 }}>
                    模板内容 *
                  </Header>
                  <Button
                    icon='copy'
                    content='复制模板'
                    size='small'
                    onClick={() => {
                      navigator.clipboard.writeText(createStepData.template);
                      showSuccess('模板已复制到剪贴板');
                    }}
                  />
                </div>
                <CodeEditor
                  value={createStepData.template}
                  onChange={(e) => setCreateStepData({ ...createStepData, template: e.target.value })}
                  placeholder='输入 Prompt 模板，支持 Go template 语法...'
                  className='template-textarea'
                  rows={25}
                  minHeight='500px'
                />
              </Segment>

              {/* 参数使用分析 */}
              {createStepData.parameters.extractors.length > 0 && (
                <Segment style={{ marginTop: '20px' }}>
                  <Header as='h5'>参数使用分析</Header>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    {createStepData.parameters.extractors.map((ext, idx) => (
                      <div key={idx} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: usedParams.includes(ext.name) ? '#e8f5e9' : '#fff3e0',
                        borderRadius: '4px'
                      }}>
                        <Icon 
                          name={usedParams.includes(ext.name) ? 'check circle' : 'warning circle'} 
                          color={usedParams.includes(ext.name) ? 'green' : 'orange'} 
                        />
                        <strong>{ext.name}</strong>
                        {usedParams.includes(ext.name) ? (
                          <span style={{ color: 'green', fontSize: '12px' }}>✓ 已使用</span>
                        ) : (
                          <span style={{ color: 'orange', fontSize: '12px' }}>⚠️ 未使用</span>
                        )}
                      </div>
                    ))}
                  </div>
                </Segment>
              )}
            </div>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={() => setShowCreateModal(false)}>取消</Button>
          {createStep > 1 && (
            <Button onClick={handlePrevCreateStep}>
              <Icon name='left chevron' /> 上一步
            </Button>
          )}
          {createStep < 3 ? (
            <Button primary onClick={handleNextCreateStep}>
              下一步 <Icon name='right chevron' />
            </Button>
          ) : (
            <Button primary onClick={handleFinishCreate}>
              <Icon name='check' /> 完成创建
            </Button>
          )}
        </Modal.Actions>
      </Modal>

      {/* ===== 版本参数编辑模态框 ===== */}
      <Modal open={showVersionExtractorModal} onClose={() => setShowVersionExtractorModal(false)} size='small'>
        <Modal.Header>编辑参数（版本）</Modal.Header>
        <Modal.Content>
          {currentVersionExtractor && (
            <Form>
              <Form.Field>
                <label>参数名称 *</label>
                <Form.Input
                  placeholder='例如: code'
                  value={currentVersionExtractor.name}
                  onChange={(e) => setCurrentVersionExtractor({ ...currentVersionExtractor, name: e.target.value })}
                />
                <small>在模板中使用：{'{{.参数名称}}'}</small>
              </Form.Field>

              <Form.Field>
                <label>
                  提取类型 *
                  <Popup
                    trigger={<Icon name='help circle' size='small' color='blue' style={{marginLeft: '5px'}} />}
                    content={{
                      content: (
                        <div style={{maxWidth: '300px'}}>
                          <strong>message_index:</strong> 从消息数组的指定位置提取 (最常用)<br/>
                          <strong>message_content:</strong> 按消息角色查找 (user/assistant/system)<br/>
                          <strong>json_field:</strong> 从 JSON 消息中提取字段<br/>
                          <strong>computed:</strong> 通过计算表达式提取
                        </div>
                      )
                    }}
                    hoverable
                    position='top center'
                  />
                </label>
                <Dropdown
                  selection
                  options={extractorTypeOptions}
                  value={currentVersionExtractor.type}
                  onChange={(e, data) => setCurrentVersionExtractor({ ...currentVersionExtractor, type: data.value })}
                />
                {currentVersionExtractor.type === 'message_index' && (
                  <small style={{display: 'block', marginTop: '8px', color: '#666'}}>
                    💡 从消息数组中按位置提取。例如：索引 0 = 第 1 条消息，索引 1 = 第 2 条消息。
                  </small>
                )}
              </Form.Field>

              {currentVersionExtractor.type === 'message_index' && (
                <Form.Field>
                  <label>消息索引 *</label>
                  <Form.Input
                    type='number'
                    value={currentVersionExtractor.index}
                    onChange={(e) => setCurrentVersionExtractor({ ...currentVersionExtractor, index: parseInt(e.target.value) || 0 })}
                  />
                </Form.Field>
              )}

              <Form.Field>
                <label>描述</label>
                <Form.Input
                  placeholder='参数的描述...'
                  value={currentVersionExtractor.description}
                  onChange={(e) => setCurrentVersionExtractor({ ...currentVersionExtractor, description: e.target.value })}
                />
              </Form.Field>

              <Form.Checkbox
                label='必需字段'
                checked={currentVersionExtractor.required}
                onChange={(e) => setCurrentVersionExtractor({ ...currentVersionExtractor, required: !currentVersionExtractor.required })}
              />
            </Form>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={() => setShowVersionExtractorModal(false)}>取消</Button>
          <Button primary onClick={handleSaveVersionExtractor}>保存</Button>
        </Modal.Actions>
      </Modal>

      {/* ===== 参数编辑模态框（创建 Prompt 用）===== */}
      <Modal open={showExtractorModal} onClose={() => setShowExtractorModal(false)} size='small'>
        <Modal.Header>编辑参数</Modal.Header>
        <Modal.Content>
          {currentExtractor && (
            <Form>
              <Form.Field>
                <label>参数名称 *</label>
                <Form.Input
                  placeholder='例如: code'
                  value={currentExtractor.name}
                  onChange={(e) => setCurrentExtractor({ ...currentExtractor, name: e.target.value })}
                />
                <small>在模板中使用：{'{{.参数名称}}'}</small>
              </Form.Field>

              <Form.Field>
                <label>
                  提取类型 *
                  <Popup
                    trigger={<Icon name='help circle' size='small' color='blue' style={{marginLeft: '5px'}} />}
                    content={{
                      content: (
                        <div style={{maxWidth: '300px'}}>
                          <strong>message_index:</strong> 从消息数组的指定位置提取 (最常用)<br/>
                          <strong>message_content:</strong> 按消息角色查找 (user/assistant/system)<br/>
                          <strong>json_field:</strong> 从 JSON 消息中提取字段<br/>
                          <strong>computed:</strong> 通过计算表达式提取
                        </div>
                      )
                    }}
                    hoverable
                    position='top center'
                  />
                </label>
                <Dropdown
                  selection
                  options={extractorTypeOptions}
                  value={currentExtractor.type}
                  onChange={(e, data) => setCurrentExtractor({ ...currentExtractor, type: data.value })}
                />
                {currentExtractor.type === 'message_index' && (
                  <small style={{display: 'block', marginTop: '8px', color: '#666'}}>
                    💡 从消息数组中按位置提取。例如：索引 0 = 第 1 条消息，索引 1 = 第 2 条消息。
                  </small>
                )}
                {currentExtractor.type === 'message_content' && (
                  <small style={{display: 'block', marginTop: '8px', color: '#666'}}>
                    💡 根据消息角色查找。不需要指定索引，会自动找到对应角色的消息。
                  </small>
                )}
                {currentExtractor.type === 'json_field' && (
                  <small style={{display: 'block', marginTop: '8px', color: '#666'}}>
                    💡 从消息的 JSON 内容中提取指定字段。需要指定消息索引和字段名。
                  </small>
                )}
                {currentExtractor.type === 'computed' && (
                  <small style={{display: 'block', marginTop: '8px', color: '#666'}}>
                    💡 通过计算表达式动态获取参数值。支持复杂的逻辑处理。
                  </small>
                )}
              </Form.Field>

              {currentExtractor.type === 'message_index' && (
                <Form.Field>
                  <label>消息索引 *</label>
                  <Form.Input
                    type='number'
                    value={currentExtractor.index}
                    onChange={(e) => setCurrentExtractor({ ...currentExtractor, index: parseInt(e.target.value) || 0 })}
                  />
                  <small>从 0 开始。0 = 第 1 条消息，1 = 第 2 条消息，以此类推。</small>
                </Form.Field>
              )}

              {currentExtractor.type === 'message_content' && (
                <Form.Field>
                  <label>消息角色 *</label>
                  <Dropdown
                    selection
                    options={[
                      { key: 'user', value: 'user', text: 'user - 用户消息' },
                      { key: 'assistant', value: 'assistant', text: 'assistant - 助手消息' },
                      { key: 'system', value: 'system', text: 'system - 系统消息' }
                    ]}
                    value={currentExtractor.source || 'user'}
                    onChange={(e, data) => setCurrentExtractor({ ...currentExtractor, source: data.value })}
                  />
                </Form.Field>
              )}

              {(currentExtractor.type === 'json_field' || currentExtractor.type === 'computed') && (
                <Form.Field>
                  <label>来源/表达式 *</label>
                  <Form.Input
                    placeholder='例如: task_config 或 len(messages) > 3'
                    value={currentExtractor.source || ''}
                    onChange={(e) => setCurrentExtractor({ ...currentExtractor, source: e.target.value })}
                  />
                </Form.Field>
              )}

              <Form.Field>
                <label>描述</label>
                <Form.Input
                  placeholder='参数的描述...'
                  value={currentExtractor.description}
                  onChange={(e) => setCurrentExtractor({ ...currentExtractor, description: e.target.value })}
                />
              </Form.Field>

              <Form.Field>
                <label>转换函数</label>
                <Dropdown
                  selection
                  clearable
                  options={transformOptions}
                  value={currentExtractor.transform}
                  onChange={(e, data) => setCurrentExtractor({ ...currentExtractor, transform: data.value || '' })}
                />
                <small>可选：对提取的值进行转换处理</small>
              </Form.Field>

              <Form.Checkbox
                label='必需字段'
                checked={currentExtractor.required}
                onChange={(e) => setCurrentExtractor({ ...currentExtractor, required: !currentExtractor.required })}
              />
            </Form>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={() => setShowExtractorModal(false)}>取消</Button>
          <Button primary onClick={handleSaveExtractor}>保存</Button>
        </Modal.Actions>
      </Modal>

      {/* ===== 版本管理模态框 ===== */}
      <Modal open={showVersionModal} onClose={() => setShowVersionModal(false)} size='large'>
        <Modal.Header>
          {selectedPrompt && `管理版本 - ${selectedPrompt.name}`}
        </Modal.Header>
        <Modal.Content scrolling>
          <Tab panes={[
            {
              menuItem: '创建新版本',
              render: () => (
                <Tab.Pane>
                  <Segment style={{ marginTop: '20px' }}>
                    {/* 版本创建多步骤流程 */}
                    
                    {/* 第一步：基本信息 */}
                    {versionStep === 1 && (
                      <div>
                        <Segment color='blue'>
                          <Header as='h4'>
                            <Icon name='info' />
                            第一步：基本信息
                          </Header>
                          <p>设置版本的基本信息。</p>
                        </Segment>

                        <Form>
                          <Form.Field>
                            <label>版本号 *</label>
                            <Form.Input
                              placeholder='例如: 1.0.0'
                              value={versionFormData.version}
                              onChange={(e) => setVersionFormData({ ...versionFormData, version: e.target.value })}
                              style={{ padding: '10px 12px' }}
                            />
                          </Form.Field>

                          <Form.Field>
                            <label>变更日志</label>
                            <Form.TextArea
                              placeholder='描述这个版本的变更...'
                              value={versionFormData.change_log}
                              onChange={(e) => setVersionFormData({ ...versionFormData, change_log: e.target.value })}
                              rows={6}
                              style={{ 
                                padding: '10px 12px',
                                minHeight: '120px',
                                resize: 'vertical'
                              }}
                            />
                          </Form.Field>
                        </Form>
                      </div>
                    )}

                    {/* 第二步：参数配置 */}
                    {versionStep === 2 && (
                      <div>
                        <Segment color='green'>
                          <Header as='h4'>
                            <Icon name='settings' />
                            第二步：参数配置
                          </Header>
                          <p>定义如何从请求消息中提取参数。至少需要一个参数。</p>
                        </Segment>

                        <Button primary onClick={handleAddVersionExtractor} style={{ marginBottom: '15px' }}>
                          <Icon name='plus' /> 添加参数
                        </Button>

                        {versionParams.extractors.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            {versionParams.extractors.map((extractor, idx) => (
                              <Segment key={idx} secondary>
                                <Grid>
                                  <Grid.Row>
                                    <Grid.Column width={12}>
                                      <strong>{extractor.name}</strong>
                                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                        类型: {extractor.type} 
                                        {extractor.type === 'message_index' && ` • 索引: ${extractor.index}`}
                                        {extractor.required && ' • 必需'}
                                        {extractor.description && ` • ${extractor.description}`}
                                      </div>
                                    </Grid.Column>
                                    <Grid.Column width={4} textAlign='right'>
                                      <Button icon='edit' size='mini' onClick={() => handleEditVersionExtractor(idx)} />
                                      <Button icon='trash' size='mini' negative onClick={() => handleDeleteVersionExtractor(idx)} />
                                    </Grid.Column>
                                  </Grid.Row>
                                </Grid>
                              </Segment>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* 第三步：模板编辑 */}
                    {versionStep === 3 && (
                      <div>
                        <Segment color='orange'>
                          <Header as='h4'>
                            <Icon name='pencil' />
                            第三步：编辑模板
                          </Header>
                          <p>使用 Go template 语法编写模板，用 <code>{'{{.参数名}}'}</code> 引用参数。点击参数名可快速插入到模板中。</p>
                        </Segment>

                        {/* 可用参数提示 - 固定在顶部 */}
                        {versionParams.extractors.length > 0 && (
                          <Segment attached='top' color='blue' style={{ marginBottom: '0' }}>
                            <Header as='h5' style={{ marginBottom: '12px' }}>
                              <Icon name='info circle' />
                              可用参数（点击插入）
                            </Header>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {versionParams.extractors.map((ext, idx) => {
                                const paramCode = `{{.${ext.name}}}`;
                                const usedInTemplate = (versionFormData.template || '').includes(paramCode);
                                return (
                                  <div
                                    key={idx}
                                    onClick={() => {
                                      const textarea = document.querySelector('.version-template-textarea');
                                      if (textarea) {
                                        const start = textarea.selectionStart;
                                        const end = textarea.selectionEnd;
                                        const text = versionFormData.template || '';
                                        const newText = text.substring(0, start) + paramCode + text.substring(end);
                                        setVersionFormData({ ...versionFormData, template: newText });
                                        // 恢复光标位置
                                        setTimeout(() => {
                                          textarea.focus();
                                          textarea.setSelectionRange(start + paramCode.length, start + paramCode.length);
                                        }, 0);
                                      }
                                    }}
                                    style={{
                                      padding: '8px 12px',
                                      backgroundColor: usedInTemplate ? '#e8f5e9' : '#fff3e0',
                                      border: `2px solid ${usedInTemplate ? '#4caf50' : '#ff9800'}`,
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'scale(1)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                  >
                                    <code style={{ 
                                      fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                                      fontSize: '13px',
                                      fontWeight: 'bold',
                                      color: '#1976d2'
                                    }}>{paramCode}</code>
                                    {ext.required ? (
                                      <Label size='mini' color='red'>必需</Label>
                                    ) : (
                                      <Label size='mini'>可选</Label>
                                    )}
                                    {usedInTemplate && (
                                      <Icon name='check circle' color='green' size='small' />
                                    )}
                                    {ext.description && (
                                      <Popup
                                        trigger={<Icon name='info circle' size='small' color='grey' />}
                                        content={ext.description}
                                        position='top center'
                                        size='small'
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <Divider />
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                              <strong>模板函数：</strong>
                              <code style={{ marginLeft: '8px' }}>{'{{upper .name}}'}</code> 转大写
                              <code style={{ marginLeft: '8px' }}>{'{{lower .name}}'}</code> 转小写
                              <code style={{ marginLeft: '8px' }}>{'{{trim .name}}'}</code> 去空格
                              <code style={{ marginLeft: '8px' }}>{'{{if .name}}...{{end}}'}</code> 条件
                            </div>
                          </Segment>
                        )}

                        {/* 模板输入框 - 全宽 */}
                        <Segment attached={versionParams.extractors.length > 0 ? 'bottom' : undefined} style={{ marginTop: '0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                              模板内容 * 
                              <span style={{ color: '#666', fontWeight: 'normal', marginLeft: '10px', fontSize: '14px' }}>
                                ({(versionFormData.template || '').length} 字符)
                              </span>
                            </label>
                            <Button
                              icon='copy'
                              content='复制模板'
                              size='small'
                              onClick={() => {
                                navigator.clipboard.writeText(versionFormData.template || '');
                                showSuccess('模板已复制到剪贴板');
                              }}
                            />
                          </div>
                          <CodeEditor
                            value={versionFormData.template || ''}
                            onChange={(e) => setVersionFormData({ ...versionFormData, template: e.target.value })}
                            placeholder='输入 Prompt 模板，支持 Go template 语法...'
                            className='version-template-textarea'
                            rows={25}
                            minHeight='500px'
                          />
                        </Segment>

                        {/* 参数使用分析 */}
                        {versionParams.extractors.length > 0 && (
                          <Segment style={{ marginTop: '20px' }}>
                            <Header as='h5'>参数使用分析</Header>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                              {versionParams.extractors.map((ext, idx) => {
                                const paramCode = `{{.${ext.name}}}`;
                                const usedInTemplate = (versionFormData.template || '').includes(paramCode);
                                return (
                                  <div key={idx} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px',
                                    padding: '6px 12px',
                                    backgroundColor: usedInTemplate ? '#e8f5e9' : '#fff3e0',
                                    borderRadius: '4px'
                                  }}>
                                    <Icon 
                                      name={usedInTemplate ? 'check circle' : 'warning circle'} 
                                      color={usedInTemplate ? 'green' : 'orange'} 
                                    />
                                    <strong>{ext.name}</strong>
                                    {usedInTemplate ? (
                                      <span style={{ color: 'green', fontSize: '12px' }}>✓ 已使用</span>
                                    ) : (
                                      <span style={{ color: 'orange', fontSize: '12px' }}>⚠️ 未使用</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </Segment>
                        )}
                      </div>
                    )}

                    {/* 步骤按钮 */}
                    <div style={{ marginTop: '20px', textAlign: 'right' }}>
                      {versionStep > 1 && (
                        <Button onClick={handlePrevVersionStep} style={{ marginRight: '10px' }}>
                          <Icon name='left chevron' /> 上一步
                        </Button>
                      )}
                      {versionStep < 3 ? (
                        <Button primary onClick={handleNextVersionStep}>
                          下一步 <Icon name='right chevron' />
                        </Button>
                      ) : (
                        <Button primary onClick={handleCreateVersion}>
                          <Icon name='check' /> 创建版本
                        </Button>
                      )}
                    </div>
                  </Segment>
                </Tab.Pane>
              )
            },
            {
              menuItem: '版本列表',
              render: () => (
                <Tab.Pane>
                  <Message info size='small' style={{ marginBottom: '15px' }}>
                    <Icon name='lightbulb' />
                    <strong>提示：</strong>点击"继承"按钮可以基于某个版本创建新版本，自动复制模板和参数配置。
                  </Message>
                  <Table celled striped size='small'>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell width={2}>版本号</Table.HeaderCell>
                        <Table.HeaderCell width={3}>创建时间</Table.HeaderCell>
                        <Table.HeaderCell width={2}>使用次数</Table.HeaderCell>
                        <Table.HeaderCell width={2}>状态</Table.HeaderCell>
                        <Table.HeaderCell width={7}>操作</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {versions.map(version => (
                        <Table.Row key={version.id} positive={version.is_active}>
                          <Table.Cell>
                            <code style={{ 
                              backgroundColor: version.is_active ? '#d4edda' : '#f8f9fa',
                              padding: '2px 6px',
                              borderRadius: '4px'
                            }}>
                              {version.version}
                            </code>
                          </Table.Cell>
                          <Table.Cell>{timestamp2string(version.created_at)}</Table.Cell>
                          <Table.Cell>
                            <Label circular size='small'>
                              {version.usage_count || 0}
                            </Label>
                          </Table.Cell>
                          <Table.Cell>
                            {version.is_active ? (
                              <Label color='green' size='small'>
                                <Icon name='check' /> 活跃
                              </Label>
                            ) : (
                              <Label size='small'>非活跃</Label>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            <Button.Group size='mini'>
                              <Popup
                                trigger={
                                  <Button icon='eye' color='blue' onClick={() => handleViewVersionDetail(version)} />
                                }
                                content='查看完整版本内容'
                                position='top center'
                                size='small'
                              />
                              <Popup
                                trigger={
                                  <Button icon='flask' color='purple' onClick={() => handleOpenTestModal(version)} />
                                }
                                content='测试此版本'
                                position='top center'
                                size='small'
                              />
                              <Popup
                                trigger={
                                  <Button icon='clone' color='teal' onClick={() => handleInheritFromVersion(version.id)} />
                                }
                                content='基于此版本创建新版本（继承模板和参数）'
                                position='top center'
                                size='small'
                              />
                              {!version.is_active && (
                                <Popup
                                  trigger={
                                    <Button icon='star' color='yellow' onClick={() => handleActivateVersion(version.id)} />
                                  }
                                  content='激活此版本'
                                  position='top center'
                                  size='small'
                                />
                              )}
                              <Popup
                                trigger={
                                  <Button 
                                    icon='trash' 
                                    negative 
                                    onClick={() => handleDeleteVersion(version.id)}
                                    disabled={version.is_active}
                                  />
                                }
                                content={version.is_active ? '无法删除活跃版本' : '删除此版本'}
                                position='top center'
                                size='small'
                              />
                            </Button.Group>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                  {versions.length === 0 && (
                    <Message warning>
                      <Message.Header>暂无版本</Message.Header>
                      <p>请在"创建新版本"标签页中创建第一个版本。</p>
                    </Message>
                  )}
                </Tab.Pane>
              )
            }
          ]} />
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={() => {
            setShowVersionModal(false);
            setVersionStep(1);
            setVersionParams({ extractors: [], validation: {}, defaults: {} });
          }}>关闭</Button>
        </Modal.Actions>
      </Modal>

      {/* ===== 版本详情查看模态框 ===== */}
      <Modal open={showVersionDetailModal} onClose={() => setShowVersionDetailModal(false)} size='fullscreen'>
        <Modal.Header>
          <Icon name='eye' />
          版本详情 - {versionDetailData?.version}
          {versionDetailData?.is_active && <Label color='green' style={{ marginLeft: '10px' }}>活跃版本</Label>}
        </Modal.Header>
        <Modal.Content scrolling style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {versionDetailData && (
            <Grid columns={2} stackable>
              {/* 左列 - 模板内容 */}
              <Grid.Column width={10}>
                <Segment>
                  <Header as='h4'>
                    <Icon name='code' />
                    Prompt 模板
                  </Header>
                  <CodeViewer 
                    code={versionDetailData.template} 
                    maxHeight='600px'
                    onCopy={() => showSuccess('模板已复制到剪贴板')}
                  />
                </Segment>
              </Grid.Column>

              {/* 右列 - 信息和参数 */}
              <Grid.Column width={6}>
                {/* 基本信息 */}
                <Segment>
                  <Header as='h4'>
                    <Icon name='info circle' />
                    基本信息
                  </Header>
                  <Table basic='very' compact>
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell><strong>版本号</strong></Table.Cell>
                        <Table.Cell><code>{versionDetailData.version}</code></Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell><strong>状态</strong></Table.Cell>
                        <Table.Cell>
                          {versionDetailData.is_active ? 
                            <Label color='green' size='small'>活跃</Label> : 
                            <Label size='small'>非活跃</Label>
                          }
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell><strong>使用次数</strong></Table.Cell>
                        <Table.Cell>{versionDetailData.usage_count || 0}</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell><strong>创建时间</strong></Table.Cell>
                        <Table.Cell>{timestamp2string(versionDetailData.created_at)}</Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table>
                </Segment>

                {/* 参数配置 */}
                <Segment>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <Header as='h4' style={{ margin: 0 }}>
                      <Icon name='settings' />
                      参数配置
                    </Header>
                    <Button
                      icon='copy'
                      content='复制参数'
                      size='small'
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(versionDetailData.parameters, null, 2));
                        showSuccess('参数配置已复制到剪贴板');
                      }}
                    />
                  </div>
                  <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '6px',
                    fontFamily: '"Fira Code", "Consolas", monospace',
                    fontSize: '12px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    border: '1px solid #e9ecef'
                  }}>
                    {JSON.stringify(versionDetailData.parameters, null, 2)}
                  </div>
                </Segment>

                {/* 变更日志 */}
                {versionDetailData.change_log && (
                  <Segment>
                    <Header as='h4'>
                      <Icon name='history' />
                      变更日志
                    </Header>
                    <div style={{
                      backgroundColor: '#fff3cd',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      border: '1px solid #ffc107',
                      lineHeight: '1.6'
                    }}>
                      {versionDetailData.change_log}
                    </div>
                  </Segment>
                )}

                {/* 快捷操作 */}
                <Segment>
                  <Header as='h4'>
                    <Icon name='lightning' />
                    快捷操作
                  </Header>
                  <Button.Group fluid vertical>
                    <Button 
                      icon='clone' 
                      content='基于此版本创建新版本'
                      color='teal'
                      onClick={() => {
                        setShowVersionDetailModal(false);
                        // 查找当前版本的完整信息
                        const currentVersion = versions.find(v => v.version === versionDetailData.version);
                        if (currentVersion) {
                          handleInheritFromVersion(currentVersion.id);
                        }
                      }}
                    />
                  </Button.Group>
                </Segment>
              </Grid.Column>
            </Grid>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowVersionDetailModal(false)}>关闭</Button>
        </Modal.Actions>
      </Modal>

      {/* ===== 测试模态框 ===== */}
      <Modal open={showTestModal} onClose={() => setShowTestModal(false)} size='fullscreen' scrolling>
        <Modal.Header>
          <Icon name='flask' />
          测试 Prompt - {selectedVersion?.version}
        </Modal.Header>
        <Modal.Content>
          {/* 模型选择部分 */}
          <Segment style={{ marginBottom: '15px', backgroundColor: '#f0f8ff' }}>
            <Header as='h4'>测试配置</Header>
            <Form>
              <Form.Field>
                <label>选择 Token *</label>
                <Dropdown
                  selection
                  fluid
                  options={availableTokens.map(t => ({
                    key: t.id,
                    value: t.key,
                    text: `${t.name} (余额: ${t.remain_quota})`
                  }))}
                  value={selectedTestToken}
                  onChange={(e, data) => setSelectedTestToken(data.value)}
                  placeholder='选择 Token'
                />
                <small style={{ marginTop: '8px', display: 'block', color: '#666' }}>
                  💡 选择一个有效的 token 用于测试 API 调用
                </small>
              </Form.Field>

              <Form.Field>
                <label>输入模型 *</label>
                <Form.Input
                  placeholder='例如: gpt-3.5-turbo, gpt-4, claude-3-opus-20240229'
                  value={selectedModel}
                  onChange={(e, { value }) => setSelectedModel(value)}
                />
                <small style={{ marginTop: '8px', display: 'block', color: '#666' }}>
                  💡 输入要测试的 AI 模型名称
                </small>
              </Form.Field>
            </Form>
          </Segment>

          <Grid stackable columns={2} style={{ marginBottom: '20px' }}>
            {/* 左列：模板和参数信息 */}
            <Grid.Column width={6}>
              <Segment>
                <Header as='h4'>Prompt 模板</Header>
                <pre style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px', 
                  fontSize: '11px', 
                  maxHeight: '120px', 
                  overflowY: 'auto',
                  border: '1px solid #ddd'
                }}>
                  {selectedVersion?.template}
                </pre>
              </Segment>

              <Segment>
                <Header as='h4'>参数列表</Header>
                {getAvailableParameters().length > 0 ? (
                  <div>
                    {getAvailableParameters().map((paramName, idx) => (
                      <div key={idx} style={{ 
                        marginBottom: '8px', 
                        padding: '10px', 
                        backgroundColor: '#f0f8ff', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        border: '1px solid #b0d4ff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <strong style={{ color: '#1e90ff' }}>{`{{.${paramName}}}`}</strong>
                        </div>
                        <Button 
                          size='mini' 
                          color='blue'
                          icon='plus'
                          onClick={() => handleAddTestMessage(paramName)}
                          title='为此参数添加输入'
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <Message info size='small'>无参数配置</Message>
                )}
              </Segment>
            </Grid.Column>

            {/* 右列：测试消息输入 */}
            <Grid.Column width={10}>
              <Segment>
                <Header as='h4'>
                  <Icon name='edit' />
                  测试消息输入
                </Header>

                {testMessages.length > 0 ? (
                  <div style={{ marginBottom: '15px' }}>
                    {testMessages.map((msg, idx) => (
                      <Segment key={idx} secondary style={{ marginBottom: '12px' }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          marginBottom: '8px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <Label color='blue' ribbon>
                              <Icon name='hashtag' />
                              参数 {idx + 1}: {msg.param_name}
                            </Label>
                          </div>
                          <Button
                            icon='trash'
                            size='mini'
                            negative
                            compact
                            onClick={() => handleRemoveTestMessage(idx)}
                            title='删除此参数'
                          />
                        </div>
                        <Form.TextArea
                          value={msg.content}
                          onChange={(e) => handleUpdateTestMessage(idx, e.target.value)}
                          placeholder='输入此参数的消息内容...'
                          rows={3}
                          style={{ 
                            fontFamily: '"Courier New", monospace', 
                            fontSize: '12px',
                            marginTop: '8px'
                          }}
                        />
                      </Segment>
                    ))}
                  </div>
                ) : (
                  <Message info>
                    <Message.Header>还没有添加任何参数</Message.Header>
                    <p>请从左侧参数列表中点击"+"按钮添加参数。</p>
                  </Message>
                )}
              </Segment>

              {/* 测试结果 */}
              {testResult && (
                <Segment color='blue' style={{ marginTop: '15px' }}>
                  <Header as='h4'>
                    <Icon name='check circle' color='green' />
                    测试结果
                  </Header>

                  <Segment style={{ marginBottom: '12px', backgroundColor: '#f5f5f5' }}>
                    <Header as='h5'>请求信息</Header>
                    <div style={{ fontSize: '12px', lineHeight: '1.8' }}>
                      <div><strong>API 路由：</strong> <code>/v1/dynamic_prompt/{selectedPrompt?.api_name}/{selectedPrompt?.language}/chat/completions</code></div>
                      <div><strong>选择模型：</strong> <code>{testResult.model_name}</code></div>
                      <div><strong>消息数量：</strong> {testResult.user_messages?.length || 0} 条</div>
                      <div><strong>执行耗时：</strong> {testResult.execution_time}ms</div>
                    </div>
                  </Segment>

                  {testResult.model_reply && (
                    <Segment style={{ marginTop: '12px', backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                      <Header as='h5'>
                        <Icon name='comment' color='green' />
                        模型回复
                      </Header>
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        border: '1px solid #81c784',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: '12px',
                        lineHeight: '1.6'
                      }}>
                        {testResult.model_reply}
                      </div>
                      <small style={{ marginTop: '8px', display: 'block', color: '#666' }}>
                        📊 Tokens: {testResult.tokens_used} / {testResult.tokens_total}
                      </small>
                    </Segment>
                  )}
                </Segment>
              )}
            </Grid.Column>
          </Grid>
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={() => setShowTestModal(false)}>关闭</Button>
          <Button
            primary
            onClick={handleTestPrompt}
            loading={testLoading}
            disabled={testMessages.length === 0}
            icon='play'
            labelPosition='right'
            content='执行测试'
          />
        </Modal.Actions>
      </Modal>

      {/* ===== 导入Prompt模态框 ===== */}
      <Modal open={showImportModal} onClose={() => setShowImportModal(false)}>
        <Modal.Header>
          <Icon name='upload' />
          导入Prompt
        </Modal.Header>
        <Modal.Content>
          <Segment color='blue' style={{ marginBottom: '20px' }}>
            <Header as='h4'>导入配置</Header>
            <Form>
              <Form.Field>
                <label>导入模式</label>
                <Dropdown
                  selection
                  fluid
                  options={[
                    { key: 'create', value: 'create', text: '创建新Prompt（冲突时报错）' },
                    { key: 'override', value: 'override', text: '覆盖现有Prompt' }
                  ]}
                  value={importMode}
                  onChange={(e, data) => setImportMode(data.value)}
                />
                <small style={{ marginTop: '8px', display: 'block', color: '#666' }}>
                  💡 选择导入模式: create 模式会在冲突时报错，override 模式会覆盖现有的Prompt版本
                </small>
              </Form.Field>
              <Form.Field>
                <label>选择JSON文件</label>
                <input
                  type='file'
                  accept='.json'
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      handleImportPrompts(e.target.files[0]);
                    }
                  }}
                  style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                />
                <small style={{ marginTop: '8px', display: 'block', color: '#666' }}>
                  📁 选择由本系统导出的JSON文件
                </small>
              </Form.Field>
            </Form>
          </Segment>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowImportModal(false)}>取消</Button>
        </Modal.Actions>
      </Modal>

      {/* ===== 导出Prompt模态框 ===== */}
      <Modal open={showExportModal} onClose={() => setShowExportModal(false)}>
        <Modal.Header>
          <Icon name='download' />
          导出Prompt
        </Modal.Header>
        <Modal.Content>
          <Segment color='green' style={{ marginBottom: '20px' }}>
            <Header as='h4'>选择要导出的Prompt</Header>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {prompts.length > 0 ? (
                <div>
                  {prompts.map(prompt => (
                    <Form.Checkbox
                      key={prompt.id}
                      label={`${prompt.api_name} (${prompt.name})`}
                      checked={exportSelectedIds.includes(prompt.id)}
                      onChange={(e, data) => {
                        if (data.checked) {
                          setExportSelectedIds([...exportSelectedIds, prompt.id]);
                        } else {
                          setExportSelectedIds(exportSelectedIds.filter(id => id !== prompt.id));
                        }
                      }}
                      style={{ marginBottom: '10px', paddingLeft: '20px' }}
                    />
                  ))}
                </div>
              ) : (
                <Message info size='small'>暂无Prompt</Message>
              )}
            </div>
            <small style={{ marginTop: '12px', display: 'block', color: '#666' }}>
              已选择: {exportSelectedIds.length} 个Prompt
            </small>
          </Segment>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowExportModal(false)}>取消</Button>
          <Button
            primary
            onClick={handleExportPrompts}
            disabled={exportSelectedIds.length === 0}
            icon='download'
            content='导出'
          />
        </Modal.Actions>
      </Modal>

      {/* ===== 编辑Prompt模态框 ===== */}
      <Modal open={showEditModal} onClose={() => setShowEditModal(false)}>
        <Modal.Header>编辑Prompt</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>名称</label>
              <Form.Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>描述</label>
              <Form.TextArea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </Form.Field>
            <Form.Field>
              <label>分类</label>
              <Form.Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>状态</label>
              <Dropdown
                selection
                options={statusOptions}
                value={formData.status || 1}
                onChange={(e, data) => setFormData({ ...formData, status: data.value })}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowEditModal(false)}>取消</Button>
          <Button primary onClick={handleUpdatePrompt}>保存</Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default DynamicPrompt;
